import type {
  BoxSpacing,
  CanvasElement,
  ElementAppearance,
  ElementInteraction,
  ElementLayout,
  ElementMeta,
  ElementSize,
  ElementType,
  ParagraphStyle,
  Paint,
  Stroke,
  TypographyStyle,
} from '@/components/canvas/document/publicTypes';

export type BaseElementConfig = {
  id: string;
  name?: string;
  x: number;
  y: number;
  width: number;
  height: number;
  zIndex: number;
  visible?: boolean;
  locked?: boolean;
  parentId?: string;
  rotation?: number;
  appearance?: Partial<ElementAppearance>;
  typography?: Partial<TypographyStyle>;
  paragraph?: Partial<ParagraphStyle>;
  interaction?: ElementInteraction;
};

export function boxSpacing(all = 0): BoxSpacing {
  return { top: all, right: all, bottom: all, left: all };
}

export function solidPaint(color: string, opacity?: number): Paint {
  return opacity == null ? { type: 'solid', color } : { type: 'solid', color, opacity };
}

export function stroke(
  color: string,
  width: number,
  style: Stroke['style'] = 'solid',
): Stroke {
  return { color, width, style };
}

export function defaultAppearance(patch: Partial<ElementAppearance> = {}): ElementAppearance {
  return {
    opacity: 1,
    ...patch,
  };
}

export function defaultInteraction(): ElementInteraction {
  return {
    visible: true,
    locked: false,
    selectable: true,
    draggable: true,
    resizable: true,
    rotatable: true,
    pointerEvents: 'auto',
  };
}

export function defaultTypography(patch: Partial<TypographyStyle> = {}): TypographyStyle {
  return {
    fontFamily: 'system-ui',
    fontSize: 16,
    fontWeight: 400,
    fontStyle: 'normal',
    color: '#000000',
    ...patch,
  };
}

export function defaultParagraph(patch: Partial<ParagraphStyle> = {}): ParagraphStyle {
  return {
    textAlign: 'left',
    ...patch,
  };
}

export function createBase<T extends ElementType>(
  type: T,
  config: BaseElementConfig,
): Omit<CanvasElement<T>, 'data'> {
  const meta: ElementMeta = {
    name: config.name,
  };
  const layout: ElementLayout = {
    x: config.x,
    y: config.y,
    zIndex: config.zIndex,
    parentId: config.parentId,
    rotation: config.rotation ?? 0,
  };
  const size: ElementSize = {
    width: config.width,
    height: config.height,
  };

  return {
    id: config.id,
    type,
    version: 1,
    meta,
    layout,
    size,
    appearance: defaultAppearance(config.appearance),
    typography: defaultTypography(config.typography),
    paragraph: defaultParagraph(config.paragraph),
    interaction: {
      ...defaultInteraction(),
      visible: config.visible ?? true,
      locked: config.locked ?? false,
      ...config.interaction,
    },
  };
}
